import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui';
import FileViewerModal from '../common/FileViewerModal';

interface PaymentDetailsModalProps {
  itineraryId: string;
  onClose: () => void;
}

const PaymentDetailsModal: React.FC<PaymentDetailsModalProps> = ({ itineraryId, onClose }) => {
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewerState, setViewerState] = useState<{ isOpen: boolean; fileUrl: string; fileName: string; fileType: 'pdf' | 'image' }>({ 
    isOpen: false, 
    fileUrl: '', 
    fileName: '', 
    fileType: 'image' 
  });

  useEffect(() => {
    fetchPaymentDetails();
  }, [itineraryId]);

  const fetchPaymentDetails = async () => {
    try {
      const token = sessionStorage.getItem('jwt_token');
      const cleanId = itineraryId.replace('WH_', '');
      const response = await fetch(`/api/invoice-revamp/payments-by-itinerary/${cleanId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setPayments(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching payment details:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewProof = async (paymentId: number) => {
    try {
      const token = sessionStorage.getItem('jwt_token');
      const response = await fetch(`/api/invoice-revamp/payment-proof/${paymentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.signed_url) {
        // Determine file type from URL or filename
        const url = data.signed_url;
        const fileName = data.filename || `payment_proof_${paymentId}`;
        
        // Extract file extension from URL or filename
        let fileExt = '';
        if (fileName && fileName.includes('.')) {
          fileExt = fileName.toLowerCase().split('.').pop() || '';
        } else if (url.includes('.')) {
          // Try to extract from URL before query parameters
          const urlPath = url.split('?')[0];
          fileExt = urlPath.toLowerCase().split('.').pop() || '';
        }
        
        // Default to image if we can't determine the type
        if (['jpg', 'jpeg', 'png', 'gif'].includes(fileExt)) {
          setViewerState({ isOpen: true, fileUrl: url, fileName, fileType: 'image' });
        } else if (fileExt === 'pdf') {
          setViewerState({ isOpen: true, fileUrl: url, fileName, fileType: 'pdf' });
        } else {
          // Default to image for unknown types (most payment proofs are images)
          setViewerState({ isOpen: true, fileUrl: url, fileName, fileType: 'image' });
        }
      } else {
        alert(data.message || 'No proof available');
      }
    } catch (error) {
      console.error('Error viewing proof:', error);
      alert('Error loading proof');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold">Payment Details - {itineraryId}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : payments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No payments found</div>
          ) : (
            <div className="space-y-4">
              {payments.map((payment, index) => (
                <div key={payment.id || index} className="border rounded-lg p-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <div className="text-sm text-gray-500">Date</div>
                      <div className="font-medium">{new Date(payment.payment_date).toLocaleDateString('en-GB')}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Amount</div>
                      <div className="font-medium">{payment.currency} {payment.amount_paid?.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Method</div>
                      <div className="font-medium">{payment.payment_method || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Exchange Rate</div>
                      <div className="font-medium">{payment.exchange_rate}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">IDR Equivalent</div>
                      <div className="font-medium">IDR {payment.idr_equivalent?.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500">Reference</div>
                      <div className="font-medium">{payment.payment_reference || '-'}</div>
                    </div>
                  </div>
                  {payment.remarks && (
                    <div className="mb-4">
                      <div className="text-sm text-gray-500">Remarks</div>
                      <div className="text-sm">{payment.remarks}</div>
                    </div>
                  )}
                  <div>
                    {payment.proof_url ? (
                      <Button size="sm" onClick={() => viewProof(payment.id)}>
                        📎 View Proof
                      </Button>
                    ) : (
                      <span className="text-sm text-gray-500">No proof attached</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <FileViewerModal
        isOpen={viewerState.isOpen}
        onClose={() => setViewerState({ ...viewerState, isOpen: false })}
        fileUrl={viewerState.fileUrl}
        fileName={viewerState.fileName}
        fileType={viewerState.fileType}
      />
    </div>
  );
};

export default PaymentDetailsModal;
